import { get, throttle } from 'lodash'
// import PropTypes from 'prop-types'
import React, { Component, ComponentType } from 'react'
import styled, { createGlobalStyle } from 'styled-components'

// @ts-expect-error: this version of @gnomad/... doesn't have types
import { Cursor, PositionAxisTrack, RegionViewerContext } from '@gnomad/region-viewer'
// @ts-expect-error: this version of @gnomad/... doesn't have types
import VariantTrack from '@gnomad/track-variants'
// @ts-expect-error: this version of @gnomad/... doesn't have types
import { Modal, TooltipAnchor, TooltipHint } from '@gnomad/ui'

import datasetConfig from '../../datasetConfig'
import Fetch from '../Fetch'
import StatusMessage from '../StatusMessage'
import { DEFAULT_COLUMN_GROUP_COLOR } from '../columnGroupHeadings'
import { TrackPageSection } from './TrackPage'
import VariantDetails from './VariantDetails'
import VariantFilterControls, { FilterState } from './VariantFilterControls'
import VariantTable, { SortOrder } from './VariantTable'
import getVariantTableColumns, { VariantRow } from './variantTableColumns'
import {
  DatasetId,
  ReferenceGenome,
  RenderVariantAttributes,
  VariantColumnConfig,
  VariantLollipopTrack,
  VariantLollipopTrackGroup,
} from '../Browser'
import {
  DEFAULT_VARIANT_CATEGORY_COLOR,
  DEFAULT_VARIANT_CATEGORY_OPTIONS,
  VariantCategoryOption,
} from '../variantCategories'
import { alleleFreqForRelativeRadius } from './relativeAlleleFrequencySizing'

const DOT_COLOR_ALPHA = 0.7

const withAlpha = (hexColor: string, alpha: number): string => {
  const r = parseInt(hexColor.slice(1, 3), 16)
  const g = parseInt(hexColor.slice(3, 5), 16)
  const b = parseInt(hexColor.slice(5, 7), 16)
  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

const buildVariantColor = (variantCategoryOptions: VariantCategoryOption[]) => {
  const colorsByCategory = Object.fromEntries(
    variantCategoryOptions.map((option) => [option.id, option.color])
  )
  return (variant: VariantRow) =>
    withAlpha(
      colorsByCategory[variant.consequenceCategory] || DEFAULT_VARIANT_CATEGORY_COLOR,
      DOT_COLOR_ALPHA
    )
}

const ModalStyles = createGlobalStyle`
  #variant-details-modal .modal-content {
    max-width: none !important;
  }
`

const selectGroupResult = (variants: VariantRow[], group: string) => {
  return variants
    .filter((v) => v.group_results[group])
    .map((v) => ({ ...v, group_result: v.group_results[group] }))
}

const acFieldValue = (variant: VariantRow, acField: string): number =>
  Number(get(variant, acField)) || 0

export const maxAcAcrossGroupTracks = (
  variants: VariantRow[],
  group: VariantLollipopTrackGroup
): number =>
  Math.max(
    0,
    ...variants.flatMap((v) => group.tracks.map((track) => acFieldValue(v, track.acField)))
  )

export const lollipopTrackVariants = (
  variants: VariantRow[],
  track: VariantLollipopTrack,
  maxAcInGroup: number
) =>
  variants
    .filter((v) => acFieldValue(v, track.acField) > 0)
    .map((v) => ({
      ...v,
      allele_freq: alleleFreqForRelativeRadius(acFieldValue(v, track.acField) / maxAcInGroup),
    }))

// pass in leftPanelWidth size to have it match, re-use column group header colors
const LollipopGroupHeaderBanner = styled.div<{ $color: string; $width: number }>`
  width: ${({ $width }) => $width}px;
  margin-top: 0.75em;
  margin-bottom: 0.35em;
  padding: 0.25em 0.5em;
  font-weight: bold;
  text-align: center;
  background: ${({ $color }) => $color};
`

const LollipopGroupHeader = ({ group }: { group: VariantLollipopTrackGroup }) => (
  <RegionViewerContext.Consumer>
    {({ leftPanelWidth }: { leftPanelWidth: number }) => (
      <LollipopGroupHeaderBanner
        $color={group.color || DEFAULT_COLUMN_GROUP_COLOR}
        $width={leftPanelWidth}
      >
        {group.tooltip ? (
          <TooltipAnchor tooltip={group.tooltip}>
            <TooltipHint>{group.label}</TooltipHint>
          </TooltipAnchor>
        ) : (
          group.label
        )}
      </LollipopGroupHeaderBanner>
    )}
  </RegionViewerContext.Consumer>
)

// TK: TODO: fixme: this type could possibly be GeneRow from Browser.tsx
export interface Gene {
  gene_id: string
  reference_genome: ReferenceGenome
}

interface VariantCustomFilter {
  component: ComponentType<any>
  defaultFilter: any
  applyFilter: (variants: VariantRow[], filterState: any) => VariantRow[]
}

interface VariantsInGeneProps {
  datasetId: string
  variantAnalysisGroupOptions: readonly string[]
  defaultVariantAnalysisGroup: string
  variantAnalysisGroupLabels: { [key: string]: string }
  variantSortKey?: string
  variantSortOrder?: SortOrder
  variantResultColumns: VariantColumnConfig[]
  variantCategoryOptions: VariantCategoryOption[]
  getVariantCategory?: (variant: any) => string
  variantCustomFilter: VariantCustomFilter
  additionalVariantDetailSummaryColumns?: VariantColumnConfig[]
  renderVariantAttributes: RenderVariantAttributes
  variantDetailColumns?: VariantColumnConfig[]
  variantDetailsSingleAnalysisGroup?: boolean
  renderVariantTranscriptConsequences: boolean
  variantExportNote?: string
  variantLollipopTrackGroups?: VariantLollipopTrackGroup[]
  gene: Gene
  variants: VariantRow[]
}

interface VariantsInGeneState {
  datasetId: string
  filter: FilterState
  hoveredVariant: string | null
  rowIndexLastClickedInNavigator: number | null
  renderedVariants: VariantRow[]
  selectedAnalysisGroup: string
  selectedVariant: VariantRow | null
  sortKey: string
  sortOrder: SortOrder
  visibleVariantWindow: [number, number]
}

const defaultGP2IncludedColumns = {
  pd: true,
  psp: true,
  dlb: true,
  msa: true,
}

const defaultAGBIncludedColumns = {
  deNovo: true,
  transmittedUntransmitted: true,
  caseControl: true,
}

export class VariantsInGene extends Component<VariantsInGeneProps, VariantsInGeneState> {
  static defaultProps = {
    variantAnalysisGroupLabels: {},
    variantCategoryOptions: DEFAULT_VARIANT_CATEGORY_OPTIONS,
    getVariantCategory: undefined,
    variantCustomFilter: undefined,
    renderVariantAttributes: undefined,
    additionalVariantDetailSummaryColumns: undefined,
    variantDetailColumns: undefined,
    variantDetailsSingleAnalysisGroup: false,
    renderVariantTranscriptConsequences: false,
    variantExportNote: undefined,
    variantLollipopTrackGroups: undefined,
  }

  constructor(props: VariantsInGeneProps) {
    super(props)

    const defaultFilter: FilterState = {
      includeCategories: Object.fromEntries(
        props.variantCategoryOptions.map((option) => [option.id, true])
      ),
      searchText: '',
      custom: (props.variantCustomFilter || {}).defaultFilter,
      gp2VariantColumnGroups: props.datasetId === 'GP2' ? defaultGP2IncludedColumns : undefined,
      agbVariantColumnGroups: props.datasetId === 'AGB' ? defaultAGBIncludedColumns : undefined,
    }

    const renderedVariants = this.sortVariants(
      this.filterVariants(
        selectGroupResult(props.variants, props.defaultVariantAnalysisGroup),
        defaultFilter
      ),
      {
        sortKey: props.variantSortKey || 'variant_id',
        sortOrder: props.variantSortOrder || 'ascending',
      },
      props.variantResultColumns,
      defaultFilter
    )

    this.state = {
      datasetId: props.datasetId,
      filter: defaultFilter,
      hoveredVariant: null,
      rowIndexLastClickedInNavigator: 0,
      renderedVariants,
      selectedAnalysisGroup: props.defaultVariantAnalysisGroup,
      selectedVariant: null,
      sortKey: props.variantSortKey || 'variant_id',
      sortOrder: props.variantSortOrder || 'ascending',
      visibleVariantWindow: [0, 19],
    }
  }

  onChangeAnalysisGroup = (analysisGroup: string) => {
    this.setState((state) => {
      const { variants, variantResultColumns } = this.props
      const { filter, sortKey, sortOrder } = state
      const renderedVariants = this.sortVariants(
        this.filterVariants(selectGroupResult(variants, analysisGroup), filter),
        {
          sortKey,
          sortOrder,
        },
        variantResultColumns,
        filter
      )

      return {
        renderedVariants,
        selectedAnalysisGroup: analysisGroup,
      }
    })
  }

  onChangeFilter = (newFilter: FilterState) => {
    this.setState((state) => {
      const { variants, variantResultColumns } = this.props
      const { filter, selectedAnalysisGroup, sortKey, sortOrder } = state
      const renderedVariants = this.sortVariants(
        this.filterVariants(selectGroupResult(variants, selectedAnalysisGroup), newFilter),
        {
          sortKey,
          sortOrder,
        },
        variantResultColumns,
        filter
      )

      return {
        filter: newFilter,
        renderedVariants,
      }
    })
  }

  onClickVariant = (variant: VariantRow) => {
    this.setState({ selectedVariant: variant })
  }

  onHoverVariant = (variantId: string) => {
    this.setState({ hoveredVariant: variantId })
  }

  onSort = (newSortKey: string) => {
    this.setState((state) => {
      const { variantResultColumns } = this.props
      const { renderedVariants, sortKey, filter } = state

      let newSortOrder: SortOrder = 'descending'
      if (newSortKey === sortKey) {
        newSortOrder = state.sortOrder === 'ascending' ? 'descending' : 'ascending'
      }

      // Since the filter hasn't changed, sort the currently rendered variants instead
      // of filtering the input variants.
      const sortedVariants = this.sortVariants(
        renderedVariants,
        {
          sortKey: newSortKey,
          sortOrder: newSortOrder,
        },
        variantResultColumns,
        filter
      )

      return {
        renderedVariants: sortedVariants,
        sortKey: newSortKey,
        sortOrder: newSortOrder,
      }
    })
  }

  onVisibleRowsChange = throttle(({ startIndex, stopIndex }) => {
    this.setState({ visibleVariantWindow: [startIndex, stopIndex] })
  }, 100)

  onClickPosition = (position: number) => {
    const { variantResultColumns } = this.props
    const { filter, renderedVariants } = this.state
    const sortedVariants = this.sortVariants(
      renderedVariants,
      {
        sortKey: 'variant_id',
        sortOrder: 'ascending',
      },
      variantResultColumns,
      filter
    )

    let index
    if (sortedVariants.length === 0 || position < sortedVariants[0].pos) {
      index = 0
    } else {
      index = sortedVariants.findIndex(
        (variant, i) =>
          sortedVariants[i + 1] && position >= variant.pos && position <= sortedVariants[i + 1].pos
      )

      if (index === -1) {
        index = sortedVariants.length - 1
      }
    }

    this.setState({
      renderedVariants: sortedVariants,
      rowIndexLastClickedInNavigator: index,
      sortKey: 'variant_id',
      sortOrder: 'ascending',
    })
  }

  filterVariants(variants: VariantRow[], filter: FilterState) {
    const { variantCustomFilter, datasetId } = this.props

    let filteredVariants = variants

    const isEveryVariantCategorySelected = Object.values(filter.includeCategories).every(Boolean)

    if (!isEveryVariantCategorySelected) {
      filteredVariants = variants.filter((variant) => {
        return filter.includeCategories[variant.consequenceCategory]
      })
    }

    if (filter.searchText) {
      const query = filter.searchText.toLowerCase()
      filteredVariants = filteredVariants.filter(
        (v) =>
          v.variant_id.toLowerCase().includes(query) ||
          (v.consequence || '').toLowerCase().includes(query) ||
          (v.hgvsc || '').toLowerCase().includes(query) ||
          (v.hgvsp || '').toLowerCase().includes(query)
      )
    }

    if (datasetId === 'GP2' && filter.gp2VariantColumnGroups) {
      const activeGroups = Object.keys(filter.gp2VariantColumnGroups).filter(
        (g) => filter.gp2VariantColumnGroups![g]
      )

      if (activeGroups.length === 0) {
        filteredVariants = []
      } else {
        filteredVariants = filteredVariants
          .filter((v) => {
            return activeGroups.some((group) => v.group_result[`wgs_ac_${group}`] > 0)
          })
          .map((v) => {
            const summedAlleleFreq = activeGroups.reduce((totalFreq, group) => {
              return totalFreq + (v.group_result[`wgs_af_${group}`] || 0)
            }, 0)

            return {
              ...v,
              allele_freq: summedAlleleFreq,
            }
          })
      }
    }

    if (variantCustomFilter) {
      filteredVariants = variantCustomFilter.applyFilter(filteredVariants, filter.custom)
    }

    return filteredVariants
  }

  sortVariants = (
    variants: VariantRow[],
    { sortKey, sortOrder }: { sortKey: string; sortOrder: SortOrder },
    variantResultColumns: VariantColumnConfig[],
    currentFilter: FilterState
  ): VariantRow[] => {
    const tableColumns = getVariantTableColumns({
      datasetId: this.props.datasetId,
      variantResultColumns: variantResultColumns,
      filter: currentFilter,
      variantCategoryOptions: this.props.variantCategoryOptions,
    })

    const column = tableColumns.find((c) => c.key === sortKey)

    if (!column) {
      return variants
    }

    const baseSortFunction = column.sortFunction
    const sortFunction =
      sortOrder === 'ascending' ? baseSortFunction : (a: any, b: any) => baseSortFunction(b, a)

    return [...variants].sort((variant1, variant2) => {
      const sortValue1 = get(variant1, column.sortKey)
      const sortValue2 = get(variant2, column.sortKey)

      const isMissing1 = sortValue1 === undefined || sortValue1 === null || sortValue1 === ''
      const isMissing2 = sortValue2 === undefined || sortValue2 === null || sortValue2 === ''
      if (isMissing1 && isMissing2) return 0
      if (isMissing1) return 1
      if (isMissing2) return -1
      return sortFunction(sortValue1, sortValue2)
    })
  }

  render() {
    const {
      gene,
      defaultVariantAnalysisGroup,
      variantAnalysisGroupLabels,
      variantAnalysisGroupOptions,
      variantResultColumns,
      variantCategoryOptions,
      variantCustomFilter,
      renderVariantAttributes,
      additionalVariantDetailSummaryColumns,
      variantDetailColumns,
      variantDetailsSingleAnalysisGroup,
      renderVariantTranscriptConsequences,
      variantExportNote,
      variantLollipopTrackGroups,
    } = this.props

    const {
      filter,
      hoveredVariant,
      renderedVariants,
      rowIndexLastClickedInNavigator,
      selectedAnalysisGroup,
      selectedVariant,
      sortKey,
      sortOrder,
      visibleVariantWindow,
    } = this.state

    const { datasetId } = this.state

    const cases =
      datasetId === 'GP2'
        ? renderedVariants
        : renderedVariants
            .filter((v) => v.group_result.ac_case > 0)
            .map((v) => ({ ...v, allele_freq: v.group_result.af_case }))

    const controls =
      datasetId === 'GP2'
        ? renderedVariants
        : renderedVariants
            .filter((v) => v.group_result.ac_ctrl > 0)
            .map((v) => ({ ...v, allele_freq: v.group_result.af_ctrl }))

    const currentTableColumns = getVariantTableColumns({
      datasetId,
      variantResultColumns,
      filter,
      variantCategoryOptions,
    })

    const variantColor = buildVariantColor(variantCategoryOptions)

    return (
      <>
        {variantLollipopTrackGroups ? (
          variantLollipopTrackGroups
            .filter(
              (group) => !filter.agbVariantColumnGroups || filter.agbVariantColumnGroups[group.key]
            )
            .flatMap((group) => {
              const maxAc = maxAcAcrossGroupTracks(renderedVariants, group)
              return [
                <LollipopGroupHeader key={`${group.key}-header`} group={group} />,
                ...group.tracks.map((track) => {
                  const trackVariants = lollipopTrackVariants(renderedVariants, track, maxAc)
                  return (
                    <VariantTrack
                      key={track.key}
                      title={`${track.title}\n(${trackVariants.length} variants)`}
                      variants={trackVariants}
                      variantColor={variantColor}
                      height={track.height}
                    />
                  )
                }),
              ]
            })
        ) : (
          <>
            <VariantTrack
              title={`Cases\n(${cases.length} variants)`}
              variants={cases}
              variantColor={variantColor}
            />
            <VariantTrack
              title={`Controls\n(${controls.length} variants)`}
              variants={controls}
              variantColor={variantColor}
            />
          </>
        )}
        <Cursor onClick={this.onClickPosition}>
          <VariantTrack
            title="Viewing in table"
            variants={renderedVariants
              .slice(visibleVariantWindow[0], visibleVariantWindow[1] + 1)
              .map((v) => {
                return {
                  ...v,
                  allele_freq: v.group_result.af,
                  isHighlighted: v.variant_id === hoveredVariant,
                }
              })}
            variantColor={variantColor}
          />
        </Cursor>
        <PositionAxisTrack />
        <TrackPageSection style={{ fontSize: '14px', marginTop: '1em' }}>
          <VariantFilterControls
            datasetId={datasetId as DatasetId}
            variantCategoryOptions={variantCategoryOptions}
            filter={filter}
            onChangeFilter={this.onChangeFilter}
            customFilterComponent={(variantCustomFilter || {}).component}
            geneId={gene.gene_id}
            renderedVariants={renderedVariants}
            variantTableColumns={currentTableColumns}
            variantAnalysisGroupLabels={variantAnalysisGroupLabels}
            variantAnalysisGroupOptions={variantAnalysisGroupOptions}
            selectedAnalysisGroup={selectedAnalysisGroup}
            onChangeAnalysisGroup={this.onChangeAnalysisGroup}
            exportNote={variantExportNote}
          />
          <VariantTable
            columns={currentTableColumns}
            highlightText={filter.searchText}
            onClickVariant={this.onClickVariant}
            onHoverVariant={this.onHoverVariant}
            onRequestSort={this.onSort}
            onVisibleRowsChange={this.onVisibleRowsChange}
            rowIndexLastClickedInNavigator={rowIndexLastClickedInNavigator}
            sortKey={sortKey}
            sortOrder={sortOrder}
            variants={renderedVariants}
          />
        </TrackPageSection>
        <ModalStyles />
        {selectedVariant && (
          <Modal
            id="variant-details-modal"
            size="large"
            title={`${selectedVariant.variant_id} (${gene.reference_genome})`}
            onRequestClose={() => {
              this.setState({ selectedVariant: null })
            }}
          >
            <VariantDetails
              datasetId={datasetId}
              defaultVariantAnalysisGroup={defaultVariantAnalysisGroup}
              referenceGenome={gene.reference_genome}
              variant={selectedVariant}
              variantAnalysisGroupOptions={variantAnalysisGroupOptions}
              variantAnalysisGroupLabels={variantAnalysisGroupLabels}
              variantResultColumns={variantResultColumns.filter((c) => c.showOnDetails !== false)}
              renderVariantAttributes={renderVariantAttributes}
              additionalVariantDetailSummaryColumns={additionalVariantDetailSummaryColumns}
              variantDetailColumns={variantDetailColumns}
              variantDetailsSingleAnalysisGroup={variantDetailsSingleAnalysisGroup}
              renderVariantTranscriptConsequences={renderVariantTranscriptConsequences}
              filter={filter}
            />
          </Modal>
        )}
      </>
    )
  }
}

const addSingleAF = (args: { groupResult: any; prefix: string; suffix: string }) => {
  const { groupResult, prefix, suffix } = args

  const ac = groupResult[`${prefix}ac_${suffix}`]
  const an = groupResult[`${prefix}an_${suffix}`]
  let af = null

  if (ac === null || an === null) {
    af = null
  } else if (an === 0) {
    af = 0
  } else {
    af = ac / an
  }

  groupResult[`${prefix}af_${suffix}`] = af
}

const addOverallAF = (args: { groupResult: any }) => {
  const { groupResult } = args

  const caseAC = groupResult.ac_case
  const caseAN = groupResult.an_case
  const caseAF = groupResult.af_case

  const controlAC = groupResult.ac_ctrl
  const controlAN = groupResult.an_ctrl
  const controlAF = groupResult.af_ctrl

  let overallAF = null

  if (caseAF === null || controlAF === null) {
    overallAF = null
  } else if (caseAN + controlAN === 0) {
    overallAF = 0
  } else {
    overallAF = (caseAC + controlAC) / (caseAN + controlAN)
  }

  groupResult.af = overallAF
}

const addOverallAFForGP2 = (args: { groupResult: any }) => {
  const { groupResult } = args

  const wgsPdAC = groupResult.wgs_ac_pd
  const wgsPdAN = groupResult.wgs_an_pd
  const wgsPdAF = groupResult.wgs_af_pd

  const wgsPspAC = groupResult.wgs_ac_psp
  const wgsPspAN = groupResult.wgs_an_psp
  const wgsPspAF = groupResult.wgs_af_psp

  const wgsDlbAC = groupResult.wgs_ac_dlb
  const wgsDlbAN = groupResult.wgs_an_dlb
  const wgsDlbAF = groupResult.wgs_af_dlb

  const wgsMsaAC = groupResult.wgs_ac_msa
  const wgsMsaAN = groupResult.wgs_an_msa
  const wgsMsaAF = groupResult.wgs_af_msa

  const wgsControlAC = groupResult.wgs_ac_ctrl
  const wgsControlAN = groupResult.wgs_an_ctrl
  const wgsControlAF = groupResult.wgs_af_ctrl

  let overallAF = null

  if (
    [wgsPdAF, wgsPspAF, wgsDlbAF, wgsMsaAF].every((item) => item === null) ||
    wgsControlAF === null
  ) {
    overallAF = null
  } else if (wgsPdAN + wgsPspAN + wgsDlbAN + wgsMsaAN + wgsControlAN === 0) {
    overallAF = 0
  } else {
    const overallAC = wgsPdAC + wgsPspAC + wgsDlbAC + wgsMsaAC + wgsControlAC
    const overallAN = wgsPdAN + wgsPspAN + wgsDlbAN + wgsMsaAN + wgsControlAN
    overallAF = overallAC / overallAN
  }

  groupResult.af = overallAF
}

interface VariantConsequence {
  term: string
  label?: string
  category?: string
}

interface VariantsInGeneContainerProps {
  datasetId: string
  gene: Gene
  variantConsequences: VariantConsequence[]
  variantAnalysisGroupOptions?: readonly string[]
  defaultVariantAnalysisGroup?: string
  variantResultColumns: VariantColumnConfig[]
  variantAlleleFrequencyOverride?: number
  variantDetailsSingleAnalysisGroup?: boolean
  // When provided, this determines each variant's category (and therefore how it's
  // colored and filtered) directly, instead of the default VEP-consequence-term lookup
  // built from `variantConsequences` below. AGB uses this to categorize by its own
  // Mis0/Mis1/Mis2/PTV/synonymous `info.variant_class`, which VEP terms don't capture.
  getVariantCategory?: (variant: any) => string
  [key: string]: any
}

const VariantsInGeneContainer = ({
  datasetId,
  gene,
  variantConsequences,
  variantAnalysisGroupOptions = undefined,
  defaultVariantAnalysisGroup = undefined,
  variantResultColumns,
  variantAlleleFrequencyOverride = undefined,
  variantDetailsSingleAnalysisGroup = false,
  getVariantCategory = undefined,
  ...otherProps
}: VariantsInGeneContainerProps) => {
  return (
    <Fetch path={`/gene/${gene.gene_id}/variants`}>
      {({ data, error, loading }) => {
        if (loading) {
          return <StatusMessage>Loading variants...</StatusMessage>
        }

        if (error || !(data || {}).variants) {
          return <StatusMessage>Unable to load variants</StatusMessage>
        }

        const consequences: Record<string, { label: string; category: string }> = {}
        variantConsequences.forEach((csq) => {
          consequences[csq.term] = {
            label: csq.label || csq.term,
            category: csq.category || 'other',
          }
        })

        const variants = data.variants.map(
          (variantValues: VariantRow[], sourceRowIndex: number) => {
            const variant: {
              info: Record<string, any>
              group_results: Record<string, any>
              [key: string]: any
            } = {
              info: {},
              group_results: {},
            }

            datasetConfig.variant_fields.forEach((field, fieldIndex) => {
              if (field === 'info') {
                datasetConfig.variant_info_field_names.forEach((infoField, infoFieldIndex) => {
                  variant.info[infoField] = variantValues[fieldIndex][infoFieldIndex]
                })
              } else if (field === 'group_results') {
                datasetConfig.variant_result_analysis_groups.forEach((group, groupIndex) => {
                  const groupResultValues = variantValues[fieldIndex][groupIndex]

                  if (groupResultValues) {
                    const groupResult: { [key: string]: string | number } = {}
                    datasetConfig.variant_group_result_field_names.forEach(
                      (groupResultField, groupResultFieldIndex) => {
                        groupResult[groupResultField] = groupResultValues[groupResultFieldIndex]
                      }
                    )

                    if (variantAlleleFrequencyOverride !== undefined) {
                      groupResult.af = variantAlleleFrequencyOverride
                      groupResult.af_case = variantAlleleFrequencyOverride
                      groupResult.af_ctrl = variantAlleleFrequencyOverride
                    } else if (datasetId !== 'GP2') {
                      addSingleAF({ groupResult, prefix: '', suffix: 'case' })
                      addSingleAF({ groupResult, prefix: '', suffix: 'ctrl' })
                      addOverallAF({ groupResult })
                    }

                    if (datasetId === 'GP2') {
                      addSingleAF({ groupResult, prefix: 'wgs_', suffix: 'pd' })
                      addSingleAF({ groupResult, prefix: 'wgs_', suffix: 'psp' })
                      addSingleAF({ groupResult, prefix: 'wgs_', suffix: 'dlb' })
                      addSingleAF({ groupResult, prefix: 'wgs_', suffix: 'msa' })
                      addSingleAF({ groupResult, prefix: 'wgs_', suffix: 'ctrl' })
                      addSingleAF({ groupResult, prefix: 'wgs_', suffix: 'other' })
                      addSingleAF({ groupResult, prefix: 'ces_', suffix: 'pd' })
                      addOverallAFForGP2({ groupResult })
                    }

                    variant.group_results[group] = groupResult
                  }
                })
              } else {
                variant[field] = variantValues[fieldIndex]
              }
            })

            variant.hgvs = variant.hgvsp || variant.hgvsc
            if (datasetId === 'AGB') {
              variant.annotationRowId = `${gene.gene_id}:${variant.variant_id}:${sourceRowIndex}`
            }

            const rawConsequenceTerm = variant.consequence
            variant.consequenceCategory = getVariantCategory
              ? getVariantCategory(variant)
              : (rawConsequenceTerm && (consequences[rawConsequenceTerm] || {}).category) || 'other'
            if (rawConsequenceTerm) {
              variant.consequence =
                (consequences[rawConsequenceTerm] || {}).label || rawConsequenceTerm
            }

            return variant
          }
        )

        return (
          <VariantsInGene
            {...otherProps}
            variantAnalysisGroupOptions={
              variantAnalysisGroupOptions || datasetConfig.variant_result_analysis_groups
            }
            defaultVariantAnalysisGroup={
              defaultVariantAnalysisGroup || datasetConfig.variant_result_analysis_groups[0]
            }
            gene={gene}
            variants={variants}
            datasetId={datasetId}
            variantResultColumns={variantResultColumns}
            variantDetailsSingleAnalysisGroup={variantDetailsSingleAnalysisGroup}
          />
        )
      }}
    </Fetch>
  )
}

export default VariantsInGeneContainer
